import { NavigationBaseProps } from "@/views/blocks/navigation/navigation.interfaces";
import { Component, computed } from "vue";
import { useI18n } from "vue-i18n";
import { BlockDef } from "./block.interfaces";
import { arrayField, buttonField, createBlock, descriptiveImageField } from "./blockBuilder";
import { useDefaultColors } from "./useDefaultColors";
import {
    ConstrainedNavigation,
    FullWidthNavigation,
    WithCenteredLogoNavigation,
    WithLeftAlignedNavigation,
    WithRightAlignedNavigation,
} from "@/views/blocks/navigation/navigation";
import { useFieldConfig } from "./useFieldConfig";
import { useDefaultThemeLogoWatcher } from "@/shared/themes/useDefaultThemeLogoWatcher";

export function useNavigations() {
    const { t } = useI18n();
    const { colorDefaults } = useDefaultColors();
    const { translations } = useFieldConfig();

    function createBaseNavigationBlock({
        component,
        typeId,
        name,
        extendBlock,
    }: {
        component: Component;
        typeId: string;
        name: string;
        extendBlock?: (block: ReturnType<typeof createBlock>) => ReturnType<typeof createBlock>;
    }): BlockDef {
        let block = createBlock<NavigationBaseProps>({
            typeId,
            type: "nav",
            name,
            component,
        })
            .withField(
                "logo",
                descriptiveImageField(
                    translations.value.logo,
                    {
                        src: "https://app.easy.tools/assets/easytools-logo.svg",
                        alt: "Easytools logo",
                    },
                    {
                        src: translations.value.src,
                        alt: translations.value.alt,
                    }
                )
            )
            .withArrayField(
                "items",
                arrayField(
                    translations.value.item,
                    "navigation",
                    {
                        label: { label: translations.value.label },
                        href: {
                            label: translations.value.href,
                            type: "link",
                        },
                    },
                    [
                        { label: "Home", href: "#" },
                        { label: "Features", href: "#" },
                        { label: "Pricing", href: "#" },
                        { label: "Contact", href: "#" },
                    ],
                    {
                        label: "New link",
                        href: "#",
                    }
                )
            )
            .withField(
                "button",
                buttonField(
                    translations.value.button,
                    {
                        label: "Log in",
                        href: "#",
                    },
                    {
                        label: translations.value.label,
                        href: translations.value.href,
                    }
                )
            )
            .withField(
                "callToActionButton",
                buttonField(
                    translations.value.callToActionButton,
                    {
                        label: "Get started",
                        href: "#",
                    },
                    {
                        label: translations.value.label,
                        href: translations.value.href,
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
            ]);

        if (extendBlock) {
            block = extendBlock(block);
        }

        return block.build();
    }

    const constrainedNavigationBlock = computed<BlockDef>(() =>
        createBaseNavigationBlock({
            component: ConstrainedNavigation,
            typeId: "ConstrainedNavigation",
            name: t("page.blocks.navigation.constrained"),
        })
    );

    const fullWidthNavigationBlock = computed<BlockDef>(() =>
        createBaseNavigationBlock({
            component: FullWidthNavigation,
            typeId: "FullWidthNavigation",
            name: t("page.blocks.navigation.fullWidth"),
        })
    );

    const withCenteredLogoNavigationBlock = computed<BlockDef>(() =>
        createBaseNavigationBlock({
            component: WithCenteredLogoNavigation,
            typeId: "WithCenteredLogoNavigation",
            name: t("page.blocks.navigation.withCenteredLogo"),
        })
    );

    const withRightAlignedNavigationBlock = computed<BlockDef>(() =>
        createBaseNavigationBlock({
            component: WithRightAlignedNavigation,
            typeId: "WithRightAlignedNavigation",
            name: t("page.blocks.navigation.withRightAligned"),
        })
    );

    const withLeftAlignedNavigationBlock = computed<BlockDef>(() =>
        createBaseNavigationBlock({
            component: WithLeftAlignedNavigation,
            typeId: "WithLeftAlignedNavigation",
            name: t("page.blocks.navigation.withLeftAligned"),
        })
    );

    const blocks = computed(() => [
        constrainedNavigationBlock.value,
        fullWidthNavigationBlock.value,
        withCenteredLogoNavigationBlock.value,
        withRightAlignedNavigationBlock.value,
        withLeftAlignedNavigationBlock.value,
    ]);

    // [EAS-2322] Use logo form default theme if present
    // @known-issue: sometimes (at first load?) it does not reflect the changes in the logo when useNavigations is used in the page editor before the theme is loaded
    useDefaultThemeLogoWatcher((logoSrc: string) => {
        for (const block of blocks.value) {
            const logoField = block.dataFields.find(({ value }) => value === "logo");

            // @ts-expect-error
            if (logoField?.defaultData?.data?.src) {
                // @ts-expect-error
                logoField.defaultData.data.src = logoSrc;
                // @ts-expect-error
                logoField.defaultData.data.alt = "Company logo";
            }
        }
    });

    return {
        blocks,
    };
}
